import { useCallback, useEffect, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { store, useAppDispatch, useAppSelector } from '@/store';
import { closeDebugPanel, setDebugPanelHeight } from '@/store/slices/debug/debugSlice';
import {
  selectBrowserInfo,
  selectDebugPanelHeight,
  selectIsDebugPanelOpen,
  selectLastRefreshedAt,
  selectLogEntries,
  selectLogLevel,
  selectLogNamespaces,
  selectP2PDiagnostics,
  selectSessionSnapshot,
  selectDebugStateSnapshot,
  selectStorageEstimate,
} from '@/store/selectors/debug';
import { refreshDiagnosticsThunk } from '@/store/thunks/debug/refreshDiagnosticsThunk';
import { refreshLogsThunk } from '@/store/thunks/debug/refreshLogsThunk';
import { setLogPatternThunk } from '@/store/thunks/debug/setLogPatternThunk';
import { setLogLevelThunk } from '@/store/thunks/debug/setLogLevelThunk';
import { clearLogsThunk } from '@/store/thunks/debug/clearLogsThunk';
import { refreshP2PDiagnosticsThunk } from '@/store/thunks/debug/refreshP2PDiagnosticsThunk';
import { useResizableHeight } from '@/hooks/shared/useResizableHeight';
import {
  buildDiagnosticsReport,
  buildFullReport,
  buildLogsReport,
  buildP2PReport,
  percentFree,
} from '@/utils/diagnosticsReport.utils';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';
import { DEBUG_PANEL_MIN_HEIGHT_PX, DEBUG_PANEL_POLL_INTERVAL_MS } from '@/constants';
import type { BrowserInfo, SessionSnapshot, StorageEstimate } from '@/types/Diagnostics';
import type { LogEntry } from '@/types/LogEntry';
import type { LogLevel } from '@/types/Logger';
import type { P2PDiagnostics } from '@/types/P2PDiagnostics';

export type DebugPanelTab = 'storage' | 'browser' | 'logs' | 'p2p' | 'session';

const REDUX_STATE_INDENT_SPACES = 2;

interface ResizeHandlePointerHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
}

interface UseDebugPanelState {
  isOpen: boolean;
  activeTab: DebugPanelTab;
  storageEstimate: StorageEstimate | null;
  browserInfo: BrowserInfo | null;
  percentFree: number;
  heightPx: number;
  logEntries: LogEntry[];
  logNamespaces: string[];
  logLevel: LogLevel;
  p2pDiagnostics: P2PDiagnostics | null;
  sessionSnapshot: SessionSnapshot | null;
}

interface UseDebugPanelHandlers {
  handleClose: () => void;
  handleTabChange: (tab: DebugPanelTab) => void;
  handleCopy: () => void;
  handleCopyAll: () => void;
  handleSetLogPattern: (input: { pattern: string; enabled: boolean }) => void;
  handleSetLogLevel: (level: LogLevel) => void;
  handleClearLogs: () => void;
  handleCopyReduxState: () => void;
  dragHandleProps: ResizeHandlePointerHandlers;
}

interface UseDebugPanelReturn {
  state: UseDebugPanelState;
  handlers: UseDebugPanelHandlers;
}

export function useDebugPanel(): UseDebugPanelReturn {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(selectIsDebugPanelOpen);
  const storageEstimate = useAppSelector(selectStorageEstimate);
  const browserInfo = useAppSelector(selectBrowserInfo);
  const lastRefreshedAt = useAppSelector(selectLastRefreshedAt);
  const persistedHeight = useAppSelector(selectDebugPanelHeight);
  const logEntries = useAppSelector(selectLogEntries);
  const logNamespaces = useAppSelector(selectLogNamespaces);
  const logLevel = useAppSelector(selectLogLevel);
  const p2pDiagnostics = useAppSelector(selectP2PDiagnostics);
  const sessionSnapshot = useAppSelector(selectSessionSnapshot);

  const [activeTab, setActiveTab] = useState<DebugPanelTab>('storage');

  const commitHeight = useCallback(
    (height: number): void => {
      dispatch(setDebugPanelHeight(height));
    },
    [dispatch]
  );

  const { height: heightPx, dragHandleProps } = useResizableHeight(persistedHeight, {
    minHeight: DEBUG_PANEL_MIN_HEIGHT_PX,
    onCommit: commitHeight,
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    void dispatch(refreshDiagnosticsThunk());
    void dispatch(refreshLogsThunk());
    void dispatch(refreshP2PDiagnosticsThunk());
    const intervalId = window.setInterval(() => {
      void dispatch(refreshDiagnosticsThunk());
      void dispatch(refreshLogsThunk());
      void dispatch(refreshP2PDiagnosticsThunk());
    }, DEBUG_PANEL_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen, dispatch]);

  const handleClose = useCallback((): void => {
    dispatch(closeDebugPanel());
  }, [dispatch]);

  const handleTabChange = useCallback((tab: DebugPanelTab): void => {
    setActiveTab(tab);
  }, []);

  const copyReport = useCallback((report: string): void => {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard?.writeText === undefined) {
      showErrorToast('Clipboard is not available in this browser');
      return;
    }

    void clipboard
      .writeText(report)
      .then(() => {
        showSuccessToast('Copied to clipboard');
      })
      .catch(() => {
        showErrorToast('Failed to copy to clipboard');
      });
  }, []);

  const handleCopyReduxState = useCallback((): void => {
    const snapshot = selectDebugStateSnapshot(store.getState());
    copyReport(JSON.stringify(snapshot, null, REDUX_STATE_INDENT_SPACES));
  }, [copyReport]);

  const handleCopy = useCallback((): void => {
    switch (activeTab) {
      case 'storage':
      case 'browser':
        copyReport(buildDiagnosticsReport({ storageEstimate, browserInfo, lastRefreshedAt }));
        break;
      case 'logs':
        copyReport(buildLogsReport(logEntries, lastRefreshedAt));
        break;
      case 'p2p':
        copyReport(buildP2PReport(p2pDiagnostics, lastRefreshedAt));
        break;
      case 'session':
        handleCopyReduxState();
        break;
    }
  }, [
    activeTab,
    storageEstimate,
    browserInfo,
    lastRefreshedAt,
    logEntries,
    p2pDiagnostics,
    copyReport,
    handleCopyReduxState,
  ]);

  const handleCopyAll = useCallback((): void => {
    copyReport(
      buildFullReport({
        storageEstimate,
        browserInfo,
        lastRefreshedAt,
        logEntries,
        p2pDiagnostics,
      })
    );
  }, [storageEstimate, browserInfo, lastRefreshedAt, logEntries, p2pDiagnostics, copyReport]);

  const handleSetLogPattern = useCallback(
    (input: { pattern: string; enabled: boolean }): void => {
      void dispatch(setLogPatternThunk(input));
    },
    [dispatch]
  );

  const handleSetLogLevel = useCallback(
    (level: LogLevel): void => {
      void dispatch(setLogLevelThunk(level));
    },
    [dispatch]
  );

  const handleClearLogs = useCallback((): void => {
    void dispatch(clearLogsThunk());
  }, [dispatch]);

  return {
    state: {
      isOpen,
      activeTab,
      storageEstimate,
      browserInfo,
      percentFree: percentFree(storageEstimate),
      heightPx,
      logEntries,
      logNamespaces,
      logLevel,
      p2pDiagnostics,
      sessionSnapshot,
    },
    handlers: {
      handleClose,
      handleTabChange,
      handleCopy,
      handleCopyAll,
      handleSetLogPattern,
      handleSetLogLevel,
      handleClearLogs,
      handleCopyReduxState,
      dragHandleProps,
    },
  };
}
